import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FilePdf, FileCsv, ShieldCheck, ChartLineUp } from "@phosphor-icons/react";

export default function ReportsPage() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
  }, []);

  const downloadFile = async (endpoint, filename) => {
    try {
      const res = await api.get(endpoint, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${filename} downloaded`);
    } catch (e) {
      toast.error("Failed to download report");
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-[1400px] mx-auto">
      <div className="mb-8">
        <div className="font-label text-orange-400 mb-2">AUDIT-READY EXPORTS</div>
        <h1 className="text-4xl md:text-5xl font-display font-black tracking-tight">Compliance Reports</h1>
        <p className="text-slate-400 mt-2 max-w-2xl">
          One-click, audit-ready packages for league offices, school district reviews, board meetings, and insurance carriers.
          Every credential, every status, every staff member.
        </p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="rounded-xl border border-white/10 bg-slate-900 p-5">
            <div className="font-label text-slate-500">TOTAL STAFF</div>
            <div className="kpi-number mt-2">{stats.total_staff}</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-slate-900 p-5">
            <div className="font-label text-slate-500">CREDENTIALS</div>
            <div className="kpi-number mt-2">{stats.total_credentials}</div>
          </div>
          <div className="rounded-xl border border-orange-500/25 bg-orange-500/5 p-5">
            <div className="font-label text-orange-400">COMPLIANCE</div>
            <div className="kpi-number mt-2 text-orange-300">{stats.compliance_rate}%</div>
          </div>
          <div className="rounded-xl border border-rose-500/25 bg-rose-500/5 p-5">
            <div className="font-label text-rose-400">GAPS</div>
            <div className="kpi-number mt-2 text-rose-300">{stats.expired + stats.expiring_soon}</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-white/10 bg-slate-900 p-8 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-rose-500/10 blur-3xl" />
          <div className="relative">
            <div className="w-14 h-14 rounded-2xl bg-rose-500/15 border border-rose-500/25 grid place-items-center mb-5">
              <FilePdf size={28} weight="fill" className="text-rose-400" />
            </div>
            <h2 className="text-2xl font-display font-black tracking-tight mb-2">PDF Compliance Report</h2>
            <p className="text-slate-400 mb-6 text-sm">
              Printable summary with organization branding, KPI overview, and detailed staff-credential table
              color-coded by status. Perfect for board meetings and audits.
            </p>
            <Button
              data-testid="download-pdf-button"
              onClick={() => downloadFile("/reports/compliance.pdf", `compliance_${new Date().toISOString().slice(0,10)}.pdf`)}
              className="bg-orange-500 text-white hover:bg-orange-400 font-semibold rounded-full h-11 px-6"
            >
              <FilePdf size={16} weight="bold" className="mr-2" /> Download PDF
            </Button>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-slate-900 p-8 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-orange-500/10 blur-3xl" />
          <div className="relative">
            <div className="w-14 h-14 rounded-2xl bg-orange-500/15 border border-orange-500/25 grid place-items-center mb-5">
              <FileCsv size={28} weight="fill" className="text-orange-400" />
            </div>
            <h2 className="text-2xl font-display font-black tracking-tight mb-2">CSV Data Export</h2>
            <p className="text-slate-400 mb-6 text-sm">
              Raw credential data with staff name, role, team, credential type, issuer, dates, and status.
              Import into Excel, Google Sheets, or a SIS.
            </p>
            <Button
              data-testid="download-csv-button"
              onClick={() => downloadFile("/reports/compliance.csv", `compliance_${new Date().toISOString().slice(0,10)}.csv`)}
              className="bg-orange-500 text-white hover:bg-orange-400 font-semibold rounded-full h-11 px-6"
            >
              <FileCsv size={16} weight="bold" className="mr-2" /> Download CSV
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-8 rounded-2xl border border-white/10 bg-slate-900 p-6 md:p-8">
        <div className="flex items-center gap-2 mb-4">
          <ChartLineUp size={18} className="text-orange-400" weight="bold" />
          <h3 className="font-display font-black tracking-tight text-lg">What's inside the report</h3>
        </div>
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <div className="flex gap-3">
            <ShieldCheck size={18} className="text-orange-400 shrink-0 mt-0.5" weight="bold" />
            <div>
              <div className="text-slate-100 font-semibold">Organization summary</div>
              <div className="text-slate-500">Total staff, credentials, and current compliance rate.</div>
            </div>
          </div>
          <div className="flex gap-3">
            <ShieldCheck size={18} className="text-orange-400 shrink-0 mt-0.5" weight="bold" />
            <div>
              <div className="text-slate-100 font-semibold">Per-credential detail</div>
              <div className="text-slate-500">Staff, role, credential type, issuer, expiration and live status.</div>
            </div>
          </div>
          <div className="flex gap-3">
            <ShieldCheck size={18} className="text-orange-400 shrink-0 mt-0.5" weight="bold" />
            <div>
              <div className="text-slate-100 font-semibold">Status color coding</div>
              <div className="text-slate-500">Green/yellow/red states so auditors can scan in seconds.</div>
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="mt-6 rounded-xl border border-white/10 bg-slate-900/70 p-5" data-testid="reports-disclaimer">
        <div className="font-label text-slate-500 mb-2">DISCLAIMER</div>
        <p className="text-xs text-slate-500 leading-relaxed">
          AuditReady Sports is an independent record-keeping tool. It is <span className="text-slate-400 font-semibold">not affiliated with, endorsed by, or sponsored by</span> FIFA,
          U.S. Soccer (USSF), the U.S. Center for SafeSport, the National Federation of State High School Associations (NFHS), the Board of Certification (BOC),
          or any other governing sports body. Credential names shown are used for organizational reference only. We do not verify credentials with issuing bodies —
          administrators remain fully responsible for the accuracy of records entered and for confirming validity directly with the issuing organization before an audit.
        </p>
      </div>
    </div>
  );
}
